import * as THREE from 'three';

import type { Fixture } from '../config/booth';
import { AISLE_WIDTH, BOOTH, EYE_HEIGHT } from '../config/booth';
import { type Box, fixtureBox, inBox, pushOut } from '../sim/geometry';

const SPEED = 6; // ft/s
const LOOK = 0.0022;
const TOUCH_LOOK = 0.005;

/** Where you can walk: both aisles and the booth floor, never through walls. */
function hallBounds(): Box {
  return {
    x0: 0.8,
    x1: BOOTH.width + AISLE_WIDTH - 0.8,
    z0: 0.9,
    z1: BOOTH.depth + AISLE_WIDTH - 0.8,
  };
}

export interface WalkControls {
  camera: THREE.PerspectiveCamera;
  update(dt: number): void;
  setFixtures(fixtures: Fixture[]): void;
  setEnabled(on: boolean): void;
  /** Puts the camera back in the main aisle looking at the booth. */
  reset(): void;
  /** Jumps to a spot; used by the dev console hook. */
  setPose(x: number, z: number, yawRad: number, pitchRad?: number): void;
}

/**
 * First-person walk. Desktop: WASD/arrows plus mouse-look (pointer lock on
 * click). Touch: drag the left half to move, the right half to look.
 */
export function createWalkControls(canvas: HTMLCanvasElement): WalkControls {
  const camera = new THREE.PerspectiveCamera(70, 1, 0.1, 400);
  camera.layers.enable(1); // The ceiling rig is on layer 1 so the plan view can skip it.
  let yaw = 0;
  let pitch = 0;
  let enabled = true;
  let obstacles: Box[] = [];
  const keys = new Set<string>();
  const move = new THREE.Vector2();
  let touchMove: { id: number; ox: number; oy: number } | null = null;
  let touchLook: { id: number; lx: number; ly: number } | null = null;

  function reset(): void {
    camera.position.set(
      BOOTH.width / 2 - 4,
      EYE_HEIGHT,
      BOOTH.depth + AISLE_WIDTH - 2.5,
    );
    yaw = -0.25;
    pitch = -0.02;
  }
  reset();

  window.addEventListener('keydown', (e) => {
    if (!enabled) return;
    if (e.target instanceof HTMLInputElement) return;
    keys.add(e.key.toLowerCase());
  });
  window.addEventListener('keyup', (e) => keys.delete(e.key.toLowerCase()));
  window.addEventListener('blur', () => keys.clear());

  // Pointer lock gives mouse-look; embedded frames refuse it, so dragging
  // with the mouse also turns the view.
  let mouseDrag = false;
  canvas.addEventListener('click', () => {
    if (!enabled || 'ontouchstart' in window) return;
    const req = canvas.requestPointerLock() as Promise<void> | undefined;
    req?.catch(() => undefined);
  });
  canvas.addEventListener('mousedown', (e) => {
    if (enabled && e.button === 0) mouseDrag = true;
  });
  window.addEventListener('mouseup', () => {
    mouseDrag = false;
  });
  window.addEventListener('mousemove', (e) => {
    if (!enabled) return;
    if (document.pointerLockElement !== canvas && !mouseDrag) return;
    yaw -= e.movementX * LOOK;
    pitch = THREE.MathUtils.clamp(pitch - e.movementY * LOOK, -1.2, 1.2);
  });

  canvas.addEventListener('pointerdown', (e) => {
    if (!enabled || e.pointerType !== 'touch') return;
    if (e.clientX < canvas.clientWidth / 2 && !touchMove)
      touchMove = { id: e.pointerId, ox: e.clientX, oy: e.clientY };
    else if (!touchLook)
      touchLook = { id: e.pointerId, lx: e.clientX, ly: e.clientY };
  });
  canvas.addEventListener('pointermove', (e) => {
    if (touchMove && e.pointerId === touchMove.id) {
      move
        .set((e.clientX - touchMove.ox) / 60, (e.clientY - touchMove.oy) / 60)
        .clampLength(0, 1);
    } else if (touchLook && e.pointerId === touchLook.id) {
      yaw -= (e.clientX - touchLook.lx) * TOUCH_LOOK;
      pitch = THREE.MathUtils.clamp(
        pitch - (e.clientY - touchLook.ly) * TOUCH_LOOK,
        -1.2,
        1.2,
      );
      touchLook.lx = e.clientX;
      touchLook.ly = e.clientY;
    }
  });
  const endTouch = (e: PointerEvent) => {
    if (touchMove?.id === e.pointerId) {
      touchMove = null;
      move.set(0, 0);
    }
    if (touchLook?.id === e.pointerId) touchLook = null;
  };
  canvas.addEventListener('pointerup', endTouch);
  canvas.addEventListener('pointercancel', endTouch);

  return {
    camera,
    reset,
    setPose(x, z, yawRad, pitchRad = 0) {
      camera.position.set(x, EYE_HEIGHT, z);
      yaw = yawRad;
      pitch = pitchRad;
    },
    setFixtures(fixtures) {
      obstacles = fixtures.map((f) => fixtureBox(f, 0.9));
    },
    setEnabled(on) {
      enabled = on;
      keys.clear();
      if (!on && document.pointerLockElement === canvas)
        document.exitPointerLock();
    },
    update(dt) {
      if (!enabled) return;
      const fwd =
        (keys.has('w') || keys.has('arrowup') ? 1 : 0) -
        (keys.has('s') || keys.has('arrowdown') ? 1 : 0);
      const strafe =
        (keys.has('d') || keys.has('arrowright') ? 1 : 0) -
        (keys.has('a') || keys.has('arrowleft') ? 1 : 0);
      const f = fwd || -move.y;
      const s = strafe || move.x;
      if (keys.has('q')) yaw += 1.6 * dt;
      if (keys.has('e')) yaw -= 1.6 * dt;
      const sin = Math.sin(yaw);
      const cos = Math.cos(yaw);
      const next = {
        x:
          camera.position.x +
          (-sin * f + cos * s) * SPEED * dt * (keys.has('shift') ? 1.8 : 1),
        z:
          camera.position.z +
          (-cos * f - sin * s) * SPEED * dt * (keys.has('shift') ? 1.8 : 1),
      };
      const bounds = hallBounds();
      next.x = THREE.MathUtils.clamp(next.x, bounds.x0, bounds.x1);
      next.z = THREE.MathUtils.clamp(next.z, bounds.z0, bounds.z1);
      // The return wall: inside the booth depth you cannot pass x = width.
      // The return wall on the left: inside the booth depth you cannot pass x = 0.
      if (next.z < BOOTH.depth && next.x < 0.9) next.x = 0.9;
      let p = next;
      for (const b of obstacles) if (inBox(p, b)) p = pushOut(p, b);
      camera.position.x = p.x;
      camera.position.z = p.z;
      camera.position.y = EYE_HEIGHT;
      camera.rotation.set(0, 0, 0);
      camera.rotateY(yaw);
      camera.rotateX(pitch);
    },
  };
}
